import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { ScrollText, ShieldCheck, AlertTriangle, Loader2, Download } from "lucide-react";
import Pagination from "@/components/Pagination";

/**
 * Public read-only inspector view. Token IS the auth — there's no login,
 * no cookies, no interceptor. We use a bare axios instance so the normal
 * api-lib auth redirects don't fire if the token is bad.
 */
export default function AuditView() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await axios.get(`${API_BASE}/audit-view/pdf`, {
        params: { token, days: 90 },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `accp_audit_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Shown errors are already covered by the JSON view; keep PDF button quiet on failure.
    } finally {
      setPdfBusy(false);
    }
  };

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/audit-view`, {
        params: { token, page: p, limit: 50 },
      });
      setData(r.data); setErr(""); setPage(r.data?.audit_logs?.page || 1);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load the inspection view.");
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [token]);

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#F5F3FB]">
      <div className="text-muted-foreground flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading inspection view…
      </div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-[#F5F3FB] p-6">
      <div className="accp-card p-8 max-w-md text-center" data-testid="audit-view-error">
        <AlertTriangle size={28} className="inline text-[#B71C1C] mb-2" />
        <h1 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
          Inspection link unavailable
        </h1>
        <p className="text-sm text-muted-foreground mt-2">{err}</p>
        <p className="text-xs text-muted-foreground mt-4">
          Please contact Abelia Inc. for a new link.
        </p>
      </div>
    </div>
  );

  const al = data.audit_logs || {};

  return (
    <div className="min-h-screen bg-[#F5F3FB] pb-16" data-testid="audit-view-page">
      <header className="bg-primary text-white p-6">
        <div className="max-w-5xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="accp-label text-white/80">Abelia Compliance Portal</div>
            <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>
              <ScrollText size={24} className="inline mr-2" /> Inspector view
            </h1>
            <div className="text-xs text-white/80 mt-2">
              Generated for: <b>{data.label}</b> · Access expires {new Date(data.expires_at).toLocaleString()}
            </div>
          </div>
          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-white text-sm font-semibold transition border border-white/20 disabled:opacity-50"
            data-testid="audit-view-download-pdf"
          >
            {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {pdfBusy ? "Generating…" : "Download PDF"}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        {data.compliance_score && (
          <section className="accp-card p-6" data-testid="audit-compliance">
            <div className="accp-label">Compliance score (last 30 days)</div>
            <div className="flex items-end gap-4 mt-1">
              <div className="text-5xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                {data.compliance_score.score}
                <span className="text-lg text-muted-foreground">/100</span>
              </div>
              <div className="text-2xl font-bold text-primary">Grade {data.compliance_score.grade}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {Object.entries(data.compliance_score.components || {}).map(([k, v]) => (
                <div key={k} className="rounded-xl bg-accent/40 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</div>
                  <div className="text-lg font-semibold text-primary mt-0.5">{v}<span className="text-xs text-muted-foreground">/25</span></div>
                </div>
              ))}
            </div>
          </section>
        )}

        <DataTable
          title="Active certifications"
          icon={ShieldCheck}
          rows={data.certifications || []}
          columns={[
            { k: "staff_code", l: "Staff code" },
            { k: "staff_name", l: "Staff name" },
            { k: "type", l: "Cert type" },
            { k: "status", l: "Status" },
            { k: "expires_at", l: "Expires", f: (v) => v ? v.slice(0, 10) : "—" },
          ]}
          testId="audit-certs"
        />

        <DataTable
          title="Recent incidents (last 90 days)"
          icon={AlertTriangle}
          rows={data.incidents || []}
          columns={[
            { k: "code", l: "Code" },
            { k: "when", l: "When", f: (v) => v ? v.slice(0, 10) : "—" },
            { k: "what", l: "What" },
            { k: "severity", l: "Severity" },
            { k: "status", l: "Status", f: (v) => v || "open" },
            { k: "reviewed_at", l: "Reviewed", f: (v) => v ? v.slice(0, 10) : "—" },
          ]}
          testId="audit-incidents"
        />

        <section className="accp-card overflow-hidden" data-testid="audit-logs">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <div className="accp-label">Audit log (last 90 days)</div>
              <div className="text-xs text-muted-foreground">{al.total} entries</div>
            </div>
          </div>
          <div className="accp-table-scroll">
            <table className="w-full text-sm">
              <thead className="bg-accent/30 text-left">
                <tr>
                  <th className="p-3 accp-label">When</th>
                  <th className="p-3 accp-label">Action</th>
                  <th className="p-3 accp-label">Entity</th>
                  <th className="p-3 accp-label">Code</th>
                  <th className="p-3 accp-label">Actor</th>
                </tr>
              </thead>
              <tbody>
                {(al.data || []).map((l, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 text-xs text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</td>
                    <td className="p-3 font-semibold text-primary">{l.action}</td>
                    <td className="p-3">{l.entity}</td>
                    <td className="p-3 text-muted-foreground">{l.entity_code || "—"}</td>
                    <td className="p-3 text-muted-foreground">{l.user_code || "—"}</td>
                  </tr>
                ))}
                {(al.data || []).length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No events in the last 90 days.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pages={al.pages} total={al.total} onChange={load} testId="audit-view-pagination" />
        </section>

        <footer className="text-xs text-muted-foreground text-center pt-6">
          Read-only view · Data current as of {new Date().toLocaleString()}
        </footer>
      </main>
    </div>
  );
}

function DataTable({ title, icon: Icon, rows, columns, testId }) {
  return (
    <section className="accp-card overflow-hidden" data-testid={testId}>
      <div className="p-4 border-b border-border">
        <div className="accp-label flex items-center gap-2">
          {Icon && <Icon size={13} />} {title}
        </div>
      </div>
      <div className="accp-table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-accent/30 text-left">
            <tr>{columns.map((c) => <th key={c.k} className="p-3 accp-label">{c.l}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border">
                {columns.map((c) => <td key={c.k} className="p-3">{c.f ? c.f(r[c.k]) : (r[c.k] ?? "—")}</td>)}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="p-6 text-center text-muted-foreground">None in scope.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
